import Loader from "components/DesignSystem/Loader/loader";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Search } from "components/DesignSystem/Search/Search";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { useState, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGetInfiniteAllGroupsInfiniteQuery } from "store/apis/crm";
import { CompanyGroup } from "types/Models/groups";
import { debounce } from "utils/debouncing";

const SCROLL_ID = "multi-group-filter-scroll";

export const MultiGroupFilter = ({
  updateFilter,
  values,
}: {
  updateFilter: <S extends "GROUPS">(
    name: S,
    newValue: {
      GROUPS: string[];
    }[S]
  ) => void;
  values: {
    GROUPS?: string[];
  };
}) => {
  const [groupSearchValue, setGroupSearchValue] = useState("");

  const {
    data: groupsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useGetInfiniteAllGroupsInfiniteQuery(
    { search_term: groupSearchValue },
    { refetchOnMountOrArgChange: true }
  );

  const groups = useMemo(() => {
    return (
      groupsData?.pages?.reduce((acc, page) => {
        return [...acc, ...(page.company_groups || [])];
      }, [] as CompanyGroup[]) || []
    );
  }, [groupsData?.pages]);

  const onGroupSearch = useMemo(
    () => debounce((value: string) => setGroupSearchValue(value)),
    []
  );

  const selected = values.GROUPS || [];

  const toggleGroup = (uuid: string, checked: boolean) => {
    const updated = checked
      ? [...selected, uuid]
      : selected.filter((id) => id !== uuid);
    updateFilter("GROUPS", updated);
  };

  if (isLoading) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full">
        <Loader customType="secondary" size="small" />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4 t-h-full">
      <Search
        onChange={(e) => onGroupSearch(e.target.value)}
        placeholder="Search"
        className="t-w-full"
      />
      <div id={SCROLL_ID} className="t-overflow-y-auto t-flex-1">
        <InfiniteScroll
          dataLength={groups.length}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          scrollableTarget={SCROLL_ID}
          className="t-flex t-flex-col t-gap-3"
          loader={
            <div className="t-flex t-justify-center t-items-center t-w-full t-gap-2 t-p-2">
              <span className="t-flex t-origin-center t-animate-spin">
                <LoadingIcon />
              </span>
            </div>
          }
        >
          {groups.map((group) => (
            <Checkbox
              key={group.uuid}
              onChange={(e) => toggleGroup(group.uuid, e.target.checked)}
              name={group.uuid}
              label={group.name}
              checked={selected.includes(group.uuid)}
            />
          ))}
        </InfiniteScroll>
      </div>
    </div>
  );
};
